import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { NamespaceResolverApiService } from '@vendasta/crm';
import { AccountGroupApiService } from '@vendasta/account-group';
import { LocationsService, isBrand } from '../locations/locations.service';
import { Router, GuardsCheckStart } from '@angular/router';
import { startWith, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { extractAccountGroupIdFromRoute } from './extract-account-group-id.util';

const namespaceResolutionCache = new Map<string, Observable<{ namespace: string; parentNamespace: string }>>();

export function clearNamespaceResolutionCache(): void {
  namespaceResolutionCache.clear();
}

@Injectable({ providedIn: 'root' })
export class NamespaceResolutionService {
  private readonly namespaceResolverService = inject(NamespaceResolverApiService);
  private readonly accountGroupApiService = inject(AccountGroupApiService);
  private readonly locationsService = inject(LocationsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly resolvedNamespaceData$: Observable<{ namespace: string; parentNamespace: string }>;

  constructor() {
    const accountGroupIdFromRoute$ = this.router.events.pipe(
      startWith(location.pathname),
      filter((event) => {
        return event instanceof GuardsCheckStart || typeof event === 'string';
      }),
      map((event) => {
        let url = '';
        if (typeof event === 'string') {
          url = event as string;
        } else {
          url = (event as GuardsCheckStart).url;
        }
        return extractAccountGroupIdFromRoute(url);
      }),
      // when coming from a page outside of the CRM module
      // the provider won't have initialized yet
      // and location.pathname won't be matching the regex.
      debounceTime(100),
    );

    const originalNamespace$ = this.locationsService.currentLocation$.pipe(
      filter((location) => location !== null),
      switchMap((location) => {
        if (isBrand(location)) {
          return accountGroupIdFromRoute$;
        } else {
          return of(location.accountGroupId);
        }
      }),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.resolvedNamespaceData$ = originalNamespace$.pipe(
      switchMap((ns) => this.getCachedNamespaceResolution(ns)),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getCachedNamespaceResolution(namespace: string): Observable<{ namespace: string; parentNamespace: string }> {
    if (!namespace) {
      return of({ namespace: '', parentNamespace: '' });
    }
    if (!namespaceResolutionCache.has(namespace)) {
      const cached$ = this.createNamespaceResolutionPipeline(namespace);
      namespaceResolutionCache.set(namespace, cached$);
    }
    return namespaceResolutionCache.get(namespace)!;
  }

  private createNamespaceResolutionPipeline(
    namespace: string,
  ): Observable<{ namespace: string; parentNamespace: string }> {
    return this.namespaceResolverService.resolveNamespace({ namespace }).pipe(
      map((response) => ({
        originalNamespace: response.originalNamespace,
        resolvedNamespace: response.resolvedNamespace,
        wasResolved: response.originalNamespace !== response.resolvedNamespace,
      })),
      catchError(() => of({ originalNamespace: namespace, resolvedNamespace: namespace, wasResolved: false })),
      switchMap((data) => this.fetchNamespaceAndParent(data)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private fetchNamespaceAndParent(data: {
    originalNamespace: string;
    resolvedNamespace: string;
    wasResolved: boolean;
  }): Observable<{ namespace: string; parentNamespace: string }> {
    if ((data.wasResolved && !data.resolvedNamespace.startsWith('AG-')) || data.originalNamespace === '') {
      return of({
        namespace: data.resolvedNamespace,
        parentNamespace: '',
      });
    }

    return this.accountGroupApiService
      .getMulti({
        accountGroupIds: [data.originalNamespace],
        projectionFilter: { accountGroupExternalIdentifiers: true },
      })
      .pipe(
        map((response) => ({
          namespace: data.originalNamespace,
          parentNamespace: response.accountGroups[0]?.accountGroup?.accountGroupExternalIdentifiers?.partnerId ?? '',
        })),
      );
  }

  get namespace$(): Observable<string> {
    return this.resolvedNamespaceData$.pipe(map((data) => data.namespace));
  }

  get parentNamespace$(): Observable<string> {
    return this.resolvedNamespaceData$.pipe(map((data) => data.parentNamespace));
  }
}
